import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconHelpCircle } from '@tabler/icons-react';
import JlfcModal from './JlfcModal';
import { JLFC_NESTED_MODAL_ZINDEX } from './jlfcModalConfig';
import { registerConfirmDialog } from '../lib/confirmDialog';

const ICONS = {
  warning: { Icon: IconAlertTriangle, color: 'yellow' },
  question: { Icon: IconHelpCircle, color: 'blue' },
};

/** Nettoie un éventuel lock scroll / pointer-events laissé par une modale Mantine. */
function clearScrollLocks() {
  if (typeof document === 'undefined') return;
  document.body.style.removeProperty('pointer-events');
  document.body.style.removeProperty('overflow');
  document.documentElement.style.removeProperty('pointer-events');
  for (const node of document.querySelectorAll('[data-scroll-locked]')) {
    node.removeAttribute('data-scroll-locked');
  }
}

export default function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(null);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const close = useCallback((result) => {
    setState((current) => {
      current?.resolve?.(result);
      return null;
    });
    setCheckboxChecked(false);
    // Après fermeture : libérer le body (évite menu figé si RemoveScroll reste bloqué).
    requestAnimationFrame(() => clearScrollLocks());
  }, []);

  const open = useCallback((options) => {
    setCheckboxChecked(false);
    return new Promise((resolve) => {
      setState({ ...options, resolve });
    });
  }, []);

  useEffect(() => {
    registerConfirmDialog(open);
    return () => registerConfirmDialog(null);
  }, [open]);

  const iconKey = state?.icon ?? 'warning';
  const { Icon, color } = ICONS[iconKey] || ICONS.warning;
  const requireCheckbox = Boolean(state?.requireCheckbox);
  const confirmDisabled = requireCheckbox && !checkboxChecked;

  return (
    <>
      {children}
      {state ? (
        <JlfcModal
          opened
          onClose={() => close(false)}
          title={state.title ?? 'Confirmation'}
          centered
          size="sm"
          closeOnEscape
          zIndex={JLFC_NESTED_MODAL_ZINDEX}
          footer={
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => close(false)}>
                {state.cancelLabel ?? 'Annuler'}
              </Button>
              <Button
                color={state.confirmColor ?? 'orange'}
                disabled={confirmDisabled}
                onClick={() => close(true)}
              >
                {state.confirmLabel ?? 'Confirmer'}
              </Button>
            </Group>
          }
        >
          <Stack gap="md">
            <Group align="flex-start" wrap="nowrap" gap="md">
              <ThemeIcon
                size={48}
                radius="xl"
                variant="light"
                color={color}
                style={{ flexShrink: 0 }}
              >
                <Icon size={28} stroke={1.5} />
              </ThemeIcon>
              <Text size="sm" style={{ flex: 1, paddingTop: 6 }}>
                {state.message}
              </Text>
            </Group>
            {requireCheckbox ? (
              <Checkbox
                checked={checkboxChecked}
                onChange={(e) => setCheckboxChecked(e.currentTarget.checked)}
                label={
                  state.checkboxLabel ||
                  'Je confirme avoir vérifié les informations'
                }
              />
            ) : null}
          </Stack>
        </JlfcModal>
      ) : null}
    </>
  );
}
